// src/lib/supabase-marketing.ts
//
// Allow-list Supabase client for the perch-marketing repository.
// Owner of EXT-2 in docs/marketing-split/slice-E-marketing-extraction.md.
//
// PURPOSE
// -------
// This module is the ONLY place in marketing source that imports
// "@supabase/supabase-js" or instantiates a Supabase client. Every
// other consumer talks to the database / edge functions / cached
// session through the named exports below.
//
// MARKETING POSTURE: PUBLIC, NO AUTH SURFACE
// ------------------------------------------
// Marketing has no sign-in / sign-up / password / MFA UI. Every
// outbound request goes anonymous (anon publishable key, no
// Authorization header). The single Supabase client below uses
// `persistSession: false` and `autoRefreshToken: false` so the SDK
// never:
//   - reads a session from localStorage,
//   - writes a session to localStorage,
//   - attaches a long-lived refresh-token timer,
//   - attaches a tab-sync `storage` listener.
//
// The one exception to "marketing never touches sessions" is the
// MarketingAuthRedirect component (D-RC-3 Path A; see EXT-1c). It
// needs to detect whether the user is already signed in on the app
// side (same origin in Phase A) and, if so, hard-redirect them to
// `/customer-portal`. To keep marketing free of the Supabase auth
// subsystem we do that detection by reading the session blob from
// localStorage directly via `hasMarketingSession()` — see comments
// on that export for the exact contract and the "stale token"
// trade-off.
//
// EXT-2 ALLOW-LIST (slice-E spec + 1 EXT-1c-driven addition)
// ----------------------------------------------------------
// Six data exports per slice-E:
//   - getPlanPricing
//   - getAllPlansPricing
//   - validateReferralCode
//   - submitInternshipApplication
//   - invokeContactEmail
//   - invokeInternshipNotification
//
// Plus one passive auth read added under EXT-1c coupling:
//   - hasMarketingSession  (no client, no listener — direct
//                           localStorage read)
//
// Plus one client lookup added for DB-validated client attribution:
//   - lookupClient
//
// Anything outside this allow-list (notably the wizard's
// `check-coverage` and `check-device` edge functions) is
// INTENTIONALLY NOT EXPOSED. Under EXT-3 (2026-05-11) the only
// marketing consumer of those calls — `src/pages/PlanSummary.tsx`
// — and the legacy `src/integrations/supabase/client.ts` were
// deleted outright (Option B rescope post-PER-108; route
// `/plan/:id` had become unreachable once `PlansSection.tsx`
// settled on `/plan-summary` -> `PlanChoiceSummary.tsx`). This
// module is now the SOLE Supabase surface in marketing.
//
// EXT-4 (ESLint wall) will enforce that no marketing source file
// outside this module imports from "@supabase/supabase-js".

import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    "Missing Supabase env: set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in .env or build environment.",
  );
}

// Single anonymous client. No session, no listeners.
const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

export interface PlanPricingOption {
  billing_interval: "monthly" | "annual";
  retail_price: number;
  discounted_price: number | null;
  discount_percent: number | null;
  discount_name: string | null;
  credit_builder_enabled: boolean;
  is_active: boolean;
}

export interface PlanPricingData {
  plan_id: string;
  plan_name: string;
  base_retail_price: number;
  enabled_billing_intervals: string[];
  monthly: PlanPricingOption | null;
  annual: PlanPricingOption | null;
  upfront_billing_eligible: boolean;
  upfront_months: number | null;
}

// Loose shape of `plan_billing_discount_configs` rows joined with
// `billing_discounts`. The Database type generated for this repo
// does not yet include these tables, so we type the join row
// locally rather than rely on the Postgrest type inference.
type DiscountConfigRow = {
  plan_id: string;
  billing_interval: "monthly" | "annual" | string;
  calculated_price: number | null;
  credit_builder_enabled: boolean;
  is_active: boolean;
  billing_discounts: {
    discount_name: string | null;
    discount_percent: number | null;
  } | null;
};

// Loose shape of `carrier_plan_configs` rows. We add
// `enabled_billing_intervals` because it exists in the DB but is
// not yet present in the generated Database type.
type CarrierPlanRow = {
  plan_id: string;
  retail_name: string;
  retail_price: number;
  enabled_billing_intervals: string[] | null;
  upfront_billing_eligible: boolean | null;
  upfront_months: number | null;
};

function buildPlanPricingData(
  carrierPlan: CarrierPlanRow,
  planDiscounts: DiscountConfigRow[],
): PlanPricingData {
  const monthlyConfig = planDiscounts.find((c) => c.billing_interval === "monthly");
  const monthlyOption: PlanPricingOption | null = monthlyConfig
    ? {
        billing_interval: "monthly",
        retail_price: carrierPlan.retail_price,
        discounted_price: monthlyConfig.calculated_price,
        discount_percent: monthlyConfig.billing_discounts?.discount_percent ?? null,
        discount_name: monthlyConfig.billing_discounts?.discount_name ?? null,
        credit_builder_enabled: monthlyConfig.credit_builder_enabled,
        is_active: monthlyConfig.is_active,
      }
    : {
        billing_interval: "monthly",
        retail_price: carrierPlan.retail_price,
        discounted_price: null,
        discount_percent: null,
        discount_name: null,
        // Default to enabled when no config row exists, mirroring
        // the pre-EXT-2 behavior in usePlanPricing.ts.
        credit_builder_enabled: true,
        is_active: true,
      };

  const annualConfig = planDiscounts.find((c) => c.billing_interval === "annual");
  const annualOption: PlanPricingOption | null =
    carrierPlan.enabled_billing_intervals?.includes("annual") && annualConfig
      ? {
          billing_interval: "annual",
          retail_price: carrierPlan.retail_price,
          discounted_price: annualConfig.calculated_price,
          discount_percent: annualConfig.billing_discounts?.discount_percent ?? null,
          discount_name: annualConfig.billing_discounts?.discount_name ?? null,
          credit_builder_enabled: annualConfig.credit_builder_enabled,
          is_active: annualConfig.is_active,
        }
      : null;

  return {
    plan_id: carrierPlan.plan_id,
    plan_name: carrierPlan.retail_name,
    base_retail_price: carrierPlan.retail_price,
    enabled_billing_intervals: carrierPlan.enabled_billing_intervals ?? ["monthly"],
    monthly: monthlyOption,
    annual: annualOption,
    upfront_billing_eligible: carrierPlan.upfront_billing_eligible ?? false,
    upfront_months: carrierPlan.upfront_months ?? null,
  };
}

export async function getPlanPricing(planId: string): Promise<PlanPricingData | null> {
  const { data: carrierPlan, error: carrierError } = await client
    .from("carrier_plan_configs")
    .select("*")
    .eq("plan_id", planId)
    .single();

  if (carrierError || !carrierPlan) {
    console.error("[supabase-marketing] getPlanPricing carrier error:", carrierError);
    return null;
  }

  const { data: discountConfigs, error: discountError } = await client
    .from("plan_billing_discount_configs")
    .select(
      `
      *,
      billing_discounts (
        discount_name,
        discount_percent
      )
    `,
    )
    .eq("plan_id", planId)
    .eq("is_active", true);

  if (discountError) {
    console.error("[supabase-marketing] getPlanPricing discount error:", discountError);
  }

  return buildPlanPricingData(
    carrierPlan as unknown as CarrierPlanRow,
    (discountConfigs ?? []) as unknown as DiscountConfigRow[],
  );
}

// Slice-E spec is parameterless ("SELECT carrier_plan_configs WHERE
// is_active"). We accept an optional `planIds` filter to preserve
// the existing useMultiplePlansPricing(planIds) call shape — when
// omitted, returns every active plan; when provided, narrows to
// just those plan_ids.
export async function getAllPlansPricing(
  planIds?: string[],
): Promise<Map<string, PlanPricingData>> {
  const pricingMap = new Map<string, PlanPricingData>();

  let carrierQuery = client.from("carrier_plan_configs").select("*").eq("is_active", true);
  if (planIds && planIds.length > 0) {
    carrierQuery = carrierQuery.in("plan_id", planIds);
  }
  const { data: carrierPlans, error: carrierError } = await carrierQuery;

  if (carrierError || !carrierPlans) {
    console.error("[supabase-marketing] getAllPlansPricing carrier error:", carrierError);
    return pricingMap;
  }

  let discountQuery = client
    .from("plan_billing_discount_configs")
    .select(
      `
      *,
      billing_discounts (
        discount_name,
        discount_percent
      )
    `,
    )
    .eq("is_active", true);
  if (planIds && planIds.length > 0) {
    discountQuery = discountQuery.in("plan_id", planIds);
  }
  const { data: discountConfigs, error: discountError } = await discountQuery;

  if (discountError) {
    console.error("[supabase-marketing] getAllPlansPricing discount error:", discountError);
  }

  const allDiscounts = (discountConfigs ?? []) as unknown as DiscountConfigRow[];
  for (const carrierPlan of carrierPlans as unknown as CarrierPlanRow[]) {
    const planDiscounts = allDiscounts.filter((c) => c.plan_id === carrierPlan.plan_id);
    pricingMap.set(carrierPlan.plan_id, buildPlanPricingData(carrierPlan, planDiscounts));
  }

  return pricingMap;
}

// ---------------------------------------------------------------------------
// Client lookup
// ---------------------------------------------------------------------------

export interface ClientData {
  id: string;
  name: string;
  upfront_billing_enabled: boolean;
  upfront_months: number | null;
}

// Resolves a client UUID against the clients table. Returns null if the id
// does not exist or if the query fails — callers degrade silently.
export async function lookupClient(clientId: string): Promise<ClientData | null> {
  const { data, error } = await client
    .from('clients')
    .select('id, name, upfront_billing_enabled, upfront_months')
    .eq('id', clientId)
    .maybeSingle();

  if (error) {
    console.error('[supabase-marketing] lookupClient error:', error);
    return null;
  }
  if (!data) return null;
  return data as unknown as ClientData;
}

// ---------------------------------------------------------------------------
// Affiliate referrals
// ---------------------------------------------------------------------------

// Returns the affiliate referral UUID when:
//   - The referral_code exists,
//   - The referral row is active (is_active = true),
//   - The owning affiliate_account has status = "active".
// Otherwise returns null. Errors are logged and surfaced as null;
// callers don't have to differentiate "lookup failed" from
// "code invalid".
export async function validateReferralCode(code: string): Promise<string | null> {
  const { data, error } = await client
    .from("affiliate_referral_ids")
    .select(
      `
      id,
      is_active,
      affiliate_accounts!inner(status)
    `,
    )
    .eq("referral_code", code)
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    console.error("[supabase-marketing] validateReferralCode error:", error);
    return null;
  }
  if (!data) return null;

  const affiliateAccount = data.affiliate_accounts as { status: string } | null;
  if (affiliateAccount?.status !== "active") return null;

  return data.id;
}

// ---------------------------------------------------------------------------
// Internship application
// ---------------------------------------------------------------------------

export interface InternshipPayload {
  full_name: string;
  university_name: string;
  email: string;
  instagram_handle?: string | null;
  tiktok_handle?: string | null;
  agrees_to_communications: boolean;
}

export async function submitInternshipApplication(payload: InternshipPayload): Promise<void> {
  const { error } = await client.from("internship_applications").insert({
    full_name: payload.full_name,
    university_name: payload.university_name,
    email: payload.email,
    instagram_handle: payload.instagram_handle ?? null,
    tiktok_handle: payload.tiktok_handle ?? null,
    agrees_to_communications: payload.agrees_to_communications,
  });

  if (error) throw error;
}

// Notification email runs as a fire-and-forget side effect after a
// successful insert. The payload is the same shape we use to store
// the application row so the recipient sees the same fields.
export type InternshipNotificationPayload = InternshipPayload;

export async function invokeInternshipNotification(
  payload: InternshipNotificationPayload,
): Promise<void> {
  const { error } = await client.functions.invoke("send-internship-notification", {
    body: payload,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Contact email
// ---------------------------------------------------------------------------

export interface ContactPayload {
  name: string;
  email: string;
  message: string;
}

// Returns the raw response body from the edge function so callers
// can surface server-side error details (the function returns a
// 200 with `{ error: string }` on validation failure).
export async function invokeContactEmail(
  payload: ContactPayload,
): Promise<{ data: unknown }> {
  const { data, error } = await client.functions.invoke("send-contact-email", {
    body: payload,
  });
  if (error) throw new Error(error.message || "Failed to send message");
  if ((data as { error?: string } | null)?.error) {
    throw new Error((data as { error: string }).error);
  }
  return { data };
}

// ---------------------------------------------------------------------------
// Passive session detection (D-RC-3 Path A; EXT-1c coupling)
// ---------------------------------------------------------------------------

// Reads the Supabase session token from localStorage WITHOUT
// instantiating a session-aware Supabase client. This deliberately
// avoids loading the Supabase auth subsystem on a public marketing
// site (no refresh-token timer, no tab-sync `storage` listener).
//
// Returns false on any of:
//   - SSR / pre-hydration (no `window`)
//   - Project ref unparseable from VITE_SUPABASE_URL
//   - No token in localStorage
//   - Token blob unparseable
//   - Token `expires_at` <= now (treat stale as anonymous; marketing
//     intentionally does NOT refresh — the user re-authenticates on
//     the app side)
//
// The storage key is `sb-<project-ref>-auth-token` per Supabase JS
// SDK convention (gotrue-js). If Supabase ever changes this naming
// the function returns false silently and MarketingAuthRedirect
// becomes a no-op — preferable to a hard error on a public page.
export function hasMarketingSession(): boolean {
  if (typeof window === "undefined") return false;
  const ref = extractProjectRef(SUPABASE_URL);
  if (!ref) return false;

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(`sb-${ref}-auth-token`);
  } catch {
    // Privacy mode / storage disabled.
    return false;
  }
  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw) as { expires_at?: number };
    if (typeof parsed.expires_at === "number") {
      const nowSec = Math.floor(Date.now() / 1000);
      if (parsed.expires_at <= nowSec) return false;
    }
    return true;
  } catch {
    return false;
  }
}

function extractProjectRef(supabaseUrl: string): string | null {
  try {
    const host = new URL(supabaseUrl).hostname;
    const ref = host.split(".")[0];
    return ref || null;
  } catch {
    return null;
  }
}
